/**
 * Forum API calls using fetch. Kept in community folder to avoid Babel parse issues
 * with deep imports from services/api in this route.
 */
const BASE = import.meta.env.VITE_API_URL || 'https://dev-admin.crypxa.com';

function getAuthHeaders(): HeadersInit {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null;
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  return headers;
}

export interface ForumCategory {
  id: number;
  name: string;
  slug: string;
  description: string;
  sort_order: number;
  post_count: number;
}

export interface ForumUserBrief {
  id: number;
  username: string;
  display_name: string;
  profile_picture_url?: string | null;
  is_staff?: boolean;
}

export interface ForumAttachment {
  id: number;
  file: string;
  file_url: string;
  caption?: string;
  sort_order: number;
  created_at: string;
}

export interface ForumPost {
  id: number;
  user: ForumUserBrief;
  category: ForumCategory;
  title: string;
  body: string;
  status: string;
  views: number;
  is_pinned: boolean;
  is_locked: boolean;
  attachments?: ForumAttachment[];
  created_at: string;
  updated_at: string;
  comment_count?: number;
  like_count?: number;
  liked?: boolean;
}

export function getCategories(): Promise<{ data: ForumCategory[] }> {
  return fetch(`${BASE}/api/forum/categories/`, { headers: getAuthHeaders() })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load categories'))))
    .then((data) => ({ data }));
}

export function getPosts(params: {
  category?: string;
  search?: string;
  page?: number;
  page_size?: number;
}): Promise<{ data: { results: ForumPost[]; count: number } }> {
  const sp = new URLSearchParams();
  if (params.category) sp.set('category', params.category);
  if (params.search) sp.set('search', params.search);
  if (params.page != null) sp.set('page', String(params.page));
  if (params.page_size != null) sp.set('page_size', String(params.page_size));
  const qs = sp.toString();
  const url = `${BASE}/api/forum/posts/${qs ? `?${qs}` : ''}`;
  return fetch(url, { headers: getAuthHeaders() })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load posts'))))
    .then((data) => ({ data: { results: data.results || [], count: data.count || 0 } }));
}
